import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import {
  Search, Filter, Grid3X3, List, Star, Plus, ExternalLink,
  Calendar, MapPin, ChevronDown, X, Check, GraduationCap, Clock, Info
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SchoolDetailModal from '../components/SchoolDetailModal';

function deadlineColor(dateStr) {
  if (!dateStr || dateStr === 'Rolling') return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (d - now) / (1000 * 60 * 60 * 24);
  if (diff < 0) return 'text-gray-400 bg-gray-50 border-gray-200';
  if (diff < 30) return 'text-red-600 bg-red-50 border-red-200';
  if (diff < 90) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-emerald-600 bg-emerald-50 border-emerald-200';
}

function deadlineLabel(dateStr) {
  if (!dateStr) return null;
  if (dateStr === 'Rolling') return 'Rolling';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return 'Passed';
  if (diff === 0) return 'Today';
  if (diff <= 7) return `${diff}d left`;
  if (diff <= 30) return `${Math.ceil(diff / 7)}w left`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function SchoolExplorerPage() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [region, setRegion] = useState('');
  const [deadlineType, setDeadlineType] = useState('');
  const [platform, setPlatform] = useState('');
  const [viewMode, setViewMode] = useState('card');
  const [myList, setMyList] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getSchools(),
      api.getSchoolFilters(),
      api.getMySchoolList().catch(() => ({ schools: [] })),
    ]).then(([s, f, ml]) => {
      setSchools(s.schools || []);
      setFilters(f);
      setMyList((ml.schools || []).map(s => s.name));
      setFavorites(JSON.parse(localStorage.getItem('ilc_fav_schools') || '[]'));
    }).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let result = schools;
    if (search) result = result.filter(s => s.name.toLowerCase().includes(search.toLowerCase()) || s.state?.toLowerCase().includes(search.toLowerCase()));
    if (region) result = result.filter(s => s.region === region);
    if (platform) result = result.filter(s => s.platform?.includes(platform));
    if (deadlineType === 'early_decision') result = result.filter(s => s.ed);
    else if (deadlineType === 'early_action') result = result.filter(s => s.ea);
    else if (deadlineType === 'rolling') result = result.filter(s => s.rd === 'Rolling');
    return result;
  }, [schools, search, region, platform, deadlineType]);

  const toggleFav = (name) => {
    const next = favorites.includes(name) ? favorites.filter(n => n !== name) : [...favorites, name];
    setFavorites(next);
    localStorage.setItem('ilc_fav_schools', JSON.stringify(next));
  };

  const addToList = async (school) => {
    if (myList.includes(school.name)) return;
    try {
      await api.createCollege({
        name: school.name,
        college_type: 'target',
        deadline: school.rd || school.ed || school.ea || '',
        application_portal_link: school.url || '',
        notes: `Platform: ${school.platform || 'N/A'}`,
      });
      setMyList([...myList, school.name]);
    } catch (e) {
      console.error('Failed to add school:', e);
      alert('Failed to add school. Please try again or check your connection.');
    }
  };

  const activeFilters = [region, deadlineType, platform].filter(Boolean).length;

  if (loading) return (
    <div className="space-y-4 animate-pulse">
      <div className="h-10 bg-[#E2E2E2] rounded-lg w-full max-w-md" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-[#E2E2E2] rounded-xl" />)}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="school-explorer">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>School Explorer</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} of {schools.length} schools &middot; {myList.length} on your list</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={viewMode === 'card' ? 'default' : 'outline'} onClick={() => setViewMode('card')} className={viewMode === 'card' ? 'bg-[#0F392B] text-white' : ''} data-testid="view-cards">
            <Grid3X3 className="h-4 w-4" />
          </Button>
          <Button size="sm" variant={viewMode === 'table' ? 'default' : 'outline'} onClick={() => setViewMode('table')} className={viewMode === 'table' ? 'bg-[#0F392B] text-white' : ''} data-testid="view-table">
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name or state..."
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:border-[#0F392B] focus:ring-1 focus:ring-[#0F392B] transition-all"
            data-testid="school-search"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowFilters(!showFilters)} className="relative" data-testid="toggle-filters">
          <Filter className="h-4 w-4 mr-1.5" /> Filters
          {activeFilters > 0 && <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#D4AF37] text-[#0F392B] text-[10px] font-bold rounded-full flex items-center justify-center">{activeFilters}</span>}
        </Button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <Card className="border-[#E2E2E2] p-4" data-testid="filter-panel">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Region</label>
              <select value={region} onChange={(e) => setRegion(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:border-[#0F392B]" data-testid="filter-region">
                <option value="">All Regions</option>
                {filters?.regions?.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Deadline Type</label>
              <select value={deadlineType} onChange={(e) => setDeadlineType(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:border-[#0F392B]" data-testid="filter-deadline">
                <option value="">All Types</option>
                <option value="early_decision">Early Decision</option>
                <option value="early_action">Early Action</option>
                <option value="rolling">Rolling Admission</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Platform</label>
              <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-[#E2E2E2] bg-white focus:outline-none focus:border-[#0F392B]" data-testid="filter-platform">
                <option value="">All Platforms</option>
                <option value="Common App">Common App</option>
                <option value="Coalition">Coalition</option>
                <option value="QuestBridge">QuestBridge</option>
                <option value="UC Application">UC Application</option>
              </select>
            </div>
          </div>
          {activeFilters > 0 && (
            <Button size="sm" variant="ghost" className="mt-3 text-xs text-muted-foreground" onClick={() => { setRegion(''); setDeadlineType(''); setPlatform(''); }} data-testid="clear-filters">
              <X className="h-3 w-3 mr-1" /> Clear all filters
            </Button>
          )}
        </Card>
      )}

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <GraduationCap className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No schools match your search</p>
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="school-cards">
          {filtered.map((s, i) => (
            <Card key={i} className="border-[#E2E2E2] hover:border-[#0F392B]/20 hover:shadow-md transition-all group cursor-pointer" onClick={() => setSelected(s)} data-testid={`school-card-${i}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#0F392B] truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{s.name}</h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="text-xs text-muted-foreground">{s.state} &middot; {s.region}</span>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); toggleFav(s.name); }} className="p-1 hover:bg-[#F2F2F0] rounded transition-colors" data-testid={`fav-${i}`}>
                    <Star className={`h-4 w-4 ${favorites.includes(s.name) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-muted-foreground/40'}`} />
                  </button>
                </div>
                <div className="space-y-1.5 mb-4">
                  {s.ed && <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Early Decision</span>
                    <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.ed)}`}>{deadlineLabel(s.ed)}</Badge>
                  </div>}
                  {s.ea && <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Early Action</span>
                    <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.ea)}`}>{deadlineLabel(s.ea)}</Badge>
                  </div>}
                  {s.rd && <div className="flex items-center justify-between">
                    <span className="text-[11px] text-muted-foreground">Regular Decision</span>
                    <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.rd)}`}>{deadlineLabel(s.rd)}</Badge>
                  </div>}
                </div>
                <div className="flex items-center gap-1.5 mb-4">
                  {s.platform?.split(', ').map((p, j) => (
                    <Badge key={j} variant="outline" className="text-[10px] bg-[#F9F9F7]">{p}</Badge>
                  ))}
                </div>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {myList.includes(s.name) ? (
                    <Button size="sm" variant="outline" className="flex-1 text-xs text-emerald-600 border-emerald-200 bg-emerald-50" disabled>
                      <Check className="h-3 w-3 mr-1" /> On Your List
                    </Button>
                  ) : (
                    <Button size="sm" className="flex-1 text-xs bg-[#0F392B] hover:bg-[#0F392B]/90 text-white" onClick={() => addToList(s)} data-testid={`add-${i}`}>
                      <Plus className="h-3 w-3 mr-1" /> Add to My List
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="text-xs px-2" onClick={(e) => { e.stopPropagation(); setSelected(s); }} data-testid={`details-${i}`}>
                    <Info className="h-3 w-3" />
                  </Button>
                  {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="outline" className="text-xs px-2"><ExternalLink className="h-3 w-3" /></Button>
                  </a>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="border border-[#E2E2E2] rounded-xl overflow-hidden" data-testid="school-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F9F9F7] border-b border-[#E2E2E2]">
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">School</th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">State</th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">ED</th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">EA</th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">RD</th>
                  <th className="text-left px-4 py-3 font-medium text-xs text-muted-foreground">Platform</th>
                  <th className="text-center px-4 py-3 font-medium text-xs text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={i} className="border-b border-[#E2E2E2] last:border-0 hover:bg-[#F9F9F7] transition-colors cursor-pointer" onClick={() => setSelected(s)} data-testid={`school-row-${i}`}>
                    <td className="px-4 py-3 font-medium text-[#0F392B]">{s.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{s.state}</td>
                    <td className="px-4 py-3">{s.ed ? <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.ed)}`}>{deadlineLabel(s.ed)}</Badge> : <span className="text-muted-foreground/40">—</span>}</td>
                    <td className="px-4 py-3">{s.ea ? <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.ea)}`}>{deadlineLabel(s.ea)}</Badge> : <span className="text-muted-foreground/40">—</span>}</td>
                    <td className="px-4 py-3">{s.rd ? <Badge variant="outline" className={`text-[10px] ${deadlineColor(s.rd)}`}>{deadlineLabel(s.rd)}</Badge> : <span className="text-muted-foreground/40">—</span>}</td>
                    <td className="px-4 py-3"><span className="text-xs text-muted-foreground">{s.platform}</span></td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => toggleFav(s.name)} className="p-1 hover:bg-[#F2F2F0] rounded"><Star className={`h-3.5 w-3.5 ${favorites.includes(s.name) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-muted-foreground/40'}`} /></button>
                        {myList.includes(s.name) ? (
                          <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-200 bg-emerald-50"><Check className="h-3 w-3" /></Badge>
                        ) : (
                          <button onClick={() => addToList(s)} className="p-1 hover:bg-[#0F392B]/5 rounded text-[#0F392B]"><Plus className="h-3.5 w-3.5" /></button>
                        )}
                        <button onClick={() => setSelected(s)} className="p-1 hover:bg-[#F2F2F0] rounded" data-testid={`row-details-${i}`}><Info className="h-3.5 w-3.5 text-muted-foreground" /></button>
                        {s.url && <a href={s.url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-[#F2F2F0] rounded"><ExternalLink className="h-3.5 w-3.5 text-muted-foreground" /></a>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {selected && <SchoolDetailModal school={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
